import { I18n, Translate } from 'common'
import { observer } from 'mobx-react-lite'
import { ReactElement } from 'react'
import { useSelector } from 'react-redux'
import { breakpoints } from 'src/theme/breakpoints'
import { defaultTheme } from 'src/theme'
import { Price } from 'src/ui-components'
import { RootState } from 'store'
import styled from 'styled-components'
import { getAllCartsGrandTotal } from '../store/cartSelectors'
import { SellerCartVehicle } from '../store/ISellerCart'
import { SellerCartVehicleInfo } from './SellerCartVehicleInfo'
import { useUserStore } from 'src/store/user/UserStore'

interface SellerCartSummaryProps {
  vehicles: Array<SellerCartVehicle>
  header: string
  children?: ReactElement
}

export const SellerCartSummary = observer(
  (props: SellerCartSummaryProps): ReactElement => {
    const { header, vehicles, children } = props
    const userStore = useUserStore()
    const displayExclTax = userStore?.preferences?.displayExclTax === 'true'

    const allCartsGrandTotal = useSelector((state: RootState) =>
      getAllCartsGrandTotal(state)
    )

    return (
      <Wrapper id="summaryPanelContent">
        <Title>{Translate(header)}</Title>

        {vehicles?.map((sellerCartVehicle) => (
          <SellerCartVehicleInfo
            key={JSON.stringify(sellerCartVehicle.vehicle)}
            sellerCartVehicle={sellerCartVehicle}
          />
        ))}
        <Hr />
        <Total>
          <I18n name="grandTotal" />
          <Price num={allCartsGrandTotal} />
        </Total>
        {displayExclTax && (
          <ExclTaxText>
            <I18n name="displayExclTax" />
          </ExclTaxText>
        )}
        {children}
      </Wrapper>
    )
  }
)

const Wrapper = styled.div`
  background-color: ${(p) => p.theme.colors.white};
  font-family: Roboto, serif;
  font-size: 14px;
  padding: 14px;
  @media print, (max-width: ${breakpoints.laptopMedium - 1}px) {
    padding: 25px;
  }
`

const Title = styled.div`
  font-size: 24px;
  font-family: Oswald, serif;
  font-weight: bold;
  display: flex;
  justify-content: flex-start;
  margin-bottom: 12px;
  @media (max-width: ${breakpoints.laptopMedium - 1}px) {
    font-size: 20px;
  }
`

const Total = styled.div`
  display: flex;
  justify-content: space-between;
  font-weight: bold;
  align-items: center;
  margin-top: 15px;
`

const Hr = styled.hr`
  border-top: 1px solid ${(p) => p.theme.colors.almostBlack};
  opacity: 1;
  margin-bottom: 4px;
`

const ExclTaxText = styled.div`
  font-style: italic;
  font-weight: 400;
  font-size: 11px;
  color: ${defaultTheme.colors.almostBlack};
  text-align: right;
`
